import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import type { UserProfile, Membership, PlatformAdmin } from '@/types';
import { AuthContext, type AuthState } from '@/features/auth/auth-context';
import { getErrorMessage } from '@/lib/error-utils';
import { createLogger } from '@/lib/logger';
import {
  resolveMembershipState,
  resolveActiveTenantMembership,
  type ActiveOrganizationRow,
  type ActiveTenantRow,
} from '@/features/auth/auth-memberships';

const log = createLogger('AuthProvider');

// ─── Provider ─────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    platformAdmin: null,
    isPlatformAdmin: false,
    authLoadError: null,
    memberships: [],
    activeTenantId: null,
    activeOrganizationId: null,
    activeRole: null,
    hasClientAccess: false,
    isLoading: true,
    isAuthenticated: false,
  });

  const fetchProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('users_profile')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      throw new Error('Failed to load user profile');
    }

    return data as UserProfile | null;
  }, []);

  const fetchMemberships = useCallback(async (userId: string) => {
    const { data: membershipsData, error: membershipsError } = await supabase
      .from('memberships')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (membershipsError) {
      throw new Error('Failed to load user memberships');
    }

    const memberships = (membershipsData ?? []) as Membership[];

    if (memberships.length === 0) {
      return {
        memberships: [] as Membership[],
        activeOrganizationId: null,
        activeTenantId: null,
        activeRole: null,
      };
    }

    const organizationIds = [...new Set(memberships.map((membership) => membership.organization_id))];

    const [
      { data: organizationsData, error: organizationsError },
      { data: activeTenantsData, error: activeTenantsError },
    ] = await Promise.all([
      supabase
        .from('organizations')
        .select('id, is_active')
        .in('id', organizationIds),
      supabase
        .from('tenants')
        .select('id, organization_id, is_active, created_at')
        .in('organization_id', organizationIds)
        .eq('is_active', true)
        // Keep the default tenant for org-wide admins deterministic across reloads.
        .order('organization_id', { ascending: true })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true }),
    ]);

    if (organizationsError) {
      throw new Error('Failed to load organization access');
    }

    if (activeTenantsError) {
      throw new Error('Failed to load tenant access');
    }

    return resolveMembershipState(
      memberships,
      (organizationsData ?? []) as ActiveOrganizationRow[],
      (activeTenantsData ?? []) as ActiveTenantRow[],
    );
  }, []);

  const fetchPlatformAdmin = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('platform_admins')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      throw new Error('Failed to load platform admin access');
    }

    return (data as PlatformAdmin | null) ?? null;
  }, []);

  const loadUserData = useCallback(async (user: User, session: Session) => {
    try {
      const [profile, membershipState, platformAdmin] = await Promise.all([
        fetchProfile(user.id),
        fetchMemberships(user.id),
        fetchPlatformAdmin(user.id),
      ]);

      setState({
        user,
        session,
        profile,
        platformAdmin,
        isPlatformAdmin: Boolean(platformAdmin),
        authLoadError: null,
        memberships: membershipState.memberships,
        activeTenantId: membershipState.activeTenantId,
        activeOrganizationId: membershipState.activeOrganizationId,
        activeRole: membershipState.activeRole,
        hasClientAccess: membershipState.memberships.length > 0,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      log.error('Failed to load user data', error);

      setState({
        user,
        session,
        profile: null,
        platformAdmin: null,
        isPlatformAdmin: false,
        authLoadError: getErrorMessage(error, 'Unable to load account access right now'),
        memberships: [],
        activeTenantId: null,
        activeOrganizationId: null,
        activeRole: null,
        hasClientAccess: false,
        isLoading: false,
        isAuthenticated: true,
      });
    }
  }, [fetchPlatformAdmin, fetchProfile, fetchMemberships]);

  // Set up auth listener BEFORE getSession
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          // Use setTimeout to avoid Supabase auth deadlock
          setTimeout(() => loadUserData(session.user, session), 0);
        } else {
          setState(prev => ({
            ...prev,
            user: null,
            session: null,
            profile: null,
            platformAdmin: null,
            isPlatformAdmin: false,
            authLoadError: null,
            memberships: [],
            activeTenantId: null,
            activeOrganizationId: null,
            activeRole: null,
            hasClientAccess: false,
            isLoading: false,
            isAuthenticated: false,
          }));
        }
      }
    );

    // Then check existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        loadUserData(session.user, session);
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    });

    return () => subscription.unsubscribe();
  }, [loadUserData]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const setActiveTenant = useCallback((tenantId: string) => {
    const membership = resolveActiveTenantMembership(
      state.memberships,
      tenantId,
      state.activeOrganizationId,
    );

    if (membership) {
      setState(prev => ({
        ...prev,
        activeTenantId: tenantId,
        activeOrganizationId: membership.organization_id,
        activeRole: membership.role,
      }));
    }
  }, [state.activeOrganizationId, state.memberships]);

  const refreshProfile = useCallback(async () => {
    if (state.user) {
      const profile = await fetchProfile(state.user.id);
      setState(prev => ({ ...prev, profile }));
    }
  }, [state.user, fetchProfile]);

  return (
    <AuthContext.Provider value={{
      ...state,
      signIn,
      signOut,
      resetPassword,
      updatePassword,
      setActiveTenant,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
